import request from '../../../../core/wrappers/axios-wrapper';
import { ApiURL } from '../../../../types/constants/apiUrls/api-urls';
import { RequestType } from '../../../../types/enums/request-enums';

export type WorkAssignmentAssigneeOption = {
    username: string;
    displayName: string;
};

export type WorkAssignmentCapabilities = {
    featureKey: string;
    canManageAssignments: boolean;
    canUseUnrestrictedFilter: boolean;
    currentAssignee: string;
    maxDownloadCount: number;
};

export const DEFAULT_MAX_DOWNLOAD_COUNT = 5;

export type RawImageReviewItem = {
    rawImageId: number;
    barcode: string;
    fileName: string;
    filePath: string;
    thumbnailPath: string | null;
    photoTypeCode: string;
    photoTypeDisplayName: string;
    status: string;
    assignee: string;
    createDate: string;
    availablePhotoTypeCodes: string[];
};

export type RawImageReviewListResponse = {
    items: RawImageReviewItem[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    tabCounts: Record<string, number>;
    capabilities: WorkAssignmentCapabilities;
};

export type RawImageReviewListParams = {
    pageNumber?: number;
    pageSize?: number;
    photoTypeCode?: string;
    assignee?: string;
    barcode?: string;
};

const pick = (raw: Record<string, unknown>, camel: string, pascal: string) =>
    raw[camel] ?? raw[pascal];

const resolveMaxDownloadCount = (raw: unknown): number => {
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : DEFAULT_MAX_DOWNLOAD_COUNT;
};

export const mapWorkAssignmentCapabilities = (
    raw: Record<string, unknown> | null | undefined,
): WorkAssignmentCapabilities => {
    const source = (raw ?? {}) as Record<string, unknown>;
    return {
        featureKey: String(pick(source, 'featureKey', 'FeatureKey') ?? ''),
        canManageAssignments: Boolean(pick(source, 'canManageAssignments', 'CanManageAssignments')),
        canUseUnrestrictedFilter: Boolean(
            pick(source, 'canUseUnrestrictedFilter', 'CanUseUnrestrictedFilter'),
        ),
        currentAssignee: String(pick(source, 'currentAssignee', 'CurrentAssignee') ?? ''),
        maxDownloadCount: resolveMaxDownloadCount(pick(source, 'maxDownloadCount', 'MaxDownloadCount')),
    };
};

const mapItem = (item: Record<string, unknown>): RawImageReviewItem => {
    const filePath = String(pick(item, 'filePath', 'FilePath') ?? '');
    const thumbnailRaw = pick(item, 'thumbnailPath', 'ThumbnailPath');
    const thumbnailPath =
        thumbnailRaw === null || thumbnailRaw === undefined || thumbnailRaw === ''
            ? null
            : String(thumbnailRaw);

    return {
        rawImageId: Number(pick(item, 'rawImageId', 'RawImageId') ?? 0),
        barcode: String(pick(item, 'barcode', 'Barcode') ?? ''),
        fileName: String(pick(item, 'fileName', 'FileName') ?? ''),
        filePath,
        thumbnailPath,
        photoTypeCode: String(pick(item, 'photoTypeCode', 'PhotoTypeCode') ?? ''),
        photoTypeDisplayName: String(pick(item, 'photoTypeDisplayName', 'PhotoTypeDisplayName') ?? ''),
        status: String(pick(item, 'status', 'Status') ?? ''),
        assignee: String(pick(item, 'assignee', 'Assignee') ?? ''),
        createDate: String(pick(item, 'createDate', 'CreateDate') ?? ''),
        availablePhotoTypeCodes: (
            (pick(item, 'availablePhotoTypeCodes', 'AvailablePhotoTypeCodes') ?? []) as unknown[]
        ).map(String),
    };
};

const GetItems = async (params: RawImageReviewListParams): Promise<RawImageReviewListResponse> => {
    const raw = (await request({
        url: ApiURL.Query.StudioQueryURLs.RawImageReviewQueryURL.GetItems,
        method: RequestType.GET,
        params,
    })) as Record<string, unknown>;

    const items = ((pick(raw, 'items', 'Items') ?? []) as Record<string, unknown>[]).map(mapItem);
    const tabCountsRaw = (pick(raw, 'tabCounts', 'TabCounts') ?? {}) as Record<string, number>;

    return {
        items,
        totalCount: Number(pick(raw, 'totalCount', 'TotalCount') ?? 0),
        pageNumber: Number(pick(raw, 'pageNumber', 'PageNumber') ?? 1),
        pageSize: Number(pick(raw, 'pageSize', 'PageSize') ?? 20),
        tabCounts: tabCountsRaw,
        capabilities: mapWorkAssignmentCapabilities(
            pick(raw, 'capabilities', 'Capabilities') as Record<string, unknown>,
        ),
    };
};

export const RawImageReviewQuery = {
    GetItems,
};
